import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";

import { parseWorkerNameForBadgePdf } from "../utils/badgePdf";
import { createWorkBadgePdf } from "../utils/createWorkBadgePdf";

interface UseCreateWorkBadgePdfParams {
  qualificationName: string;
  workerHeadshotBase64?: string;
  pageSize?: "A4" | [number, number];
  badgeMarginInMm?: number;
}

interface CreateWorkBadgePdfResult {
  blob: Blob;
  pdfKey: string;
}

export function useCreateWorkBadgePdf(
  options: UseMutationOptions<CreateWorkBadgePdfResult, Error, UseCreateWorkBadgePdfParams> = {}
) {
  const worker = useDefinedWorker();

  return useMutation({
    mutationFn: async ({
      qualificationName,
      workerHeadshotBase64,
      pageSize,
      badgeMarginInMm,
    }: UseCreateWorkBadgePdfParams) => {
      const workerNameDetails = parseWorkerNameForBadgePdf(worker);

      return await createWorkBadgePdf({
        workerDetails: {
          firstName: workerNameDetails.firstName,
          lastName: workerNameDetails.lastName,
          qualificationName,
          profilePictureBase64: workerHeadshotBase64,
        },
        pageSize,
        badgeMarginInMm,
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.WORK_BADGE_PDF_GENERATION_FAILURE,
    },
    ...options,
  });
}
